/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef, useState } from 'react';

import Image from 'next/image';
import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { Text } from 'ui/atoms/Text/Text';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './TransferPricingGroupLinkage.module.scss';

import { type GroupTab } from './TransferPricingGroupLinkage.types';

type Translate = (key: string) => string;

const getTabs = (t: Translate): GroupTab[] => [
  {
    label: t('Ownership'),
    stats: [
      { value: '13', label: t('entities in view') },
      { value: '11', label: t('jurisdictions') },
    ],
    foot: t('Ownership read from Companies House and the national register of each linked entity'),
    verdict: t('Every entity in this network is a related party, not an independent comparable.'),
    image: '/storage/img/platform/transfer-pricing/group-ownership.png',
    imageWidth: 769,
    imageHeight: 670,
    alt: t('Ownership network of Revolut Ltd across linked entities and jurisdictions'),
  },
  {
    label: t('Officers'),
    stats: [
      { value: '14', label: t('officers') },
      { value: '2', label: t('roles') },
    ],
    foot: t('Officers read from Companies House · appointments as filed'),
    verdict: t('Common officers surface related parties that ownership alone does not.'),
    image: '/storage/img/platform/transfer-pricing/group-officers.png',
    imageWidth: 829,
    imageHeight: 664,
    alt: t('Officer network of Revolut Ltd showing shared directors across linked entities'),
  },
];

const getBullets = (t: Translate) => [
  {
    title: t('Policy, not market'),
    description: t(
      "Benchmark against a captive and you are measuring another group's intercompany pricing, not an arm's length outcome.",
    ),
  },
  {
    title: t('Caught before selection'),
    description: t(
      'Ownership links are held globally, so the group above a candidate is visible before it reaches the set rather than after a challenge.',
    ),
  },
  {
    title: t('Works in both directions'),
    description: t(
      'The same structure maps your own group perimeter for the master file and for related-party identification.',
    ),
  },
];

export const TransferPricingGroupLinkage = () => {
  const { t } = useInt();
  const tabs = getTabs(t);
  const bullets = getBullets(t);
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => {
    const node = rootRef.current;

    if (!node || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    const loop = () => {
      setActive((prev) => (prev + 1) % tabs.length);
      timerRef.current = setTimeout(loop, 3200);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!timerRef.current) {
              timerRef.current = setTimeout(loop, 2000);
            }
          } else {
            clearTimer();
          }
        });
      },
      { threshold: 0.3 },
    );

    observer.observe(node);

    return () => {
      clearTimer();
      observer.disconnect();
    };
  }, []);

  const tab = tabs[active];

  return (
    <Section className={styles.section}>
      <div className={styles.head}>
        <TitleBlock
          align="left"
          className={styles.titleBlock}
          title={t('A captive distributor is not an independent comparable.')}
        />
      </div>

      <div className={styles.grid}>
        <div className={styles.copy}>
          <Text size="sm" className={styles.lede}>
            {t(
              "A company inside a multinational group does not price at arm's length. Its margin is the output of somebody's transfer pricing policy.",
            )}
          </Text>

          <ul className={styles.list}>
            {bullets.map((bullet) => (
              <li key={bullet.title}>
                <b>{bullet.title}</b>
                {bullet.description}
              </li>
            ))}
          </ul>
        </div>

        <div className={styles.card} ref={rootRef}>
          <div className={styles.cardHead}>
            <span className={styles.cardLabel}>Exhibit B</span>
            <span className={styles.cardName}>Revolut Ltd · 08804411</span>
            <span className={styles.cardTag}>Platform view</span>
          </div>

          <div className={styles.sub}>
            <div className={styles.tabs} role="tablist">
              {tabs.map((item, index) => (
                <button
                  key={item.label}
                  type="button"
                  role="tab"
                  aria-selected={active === index}
                  className={cn(styles.tab, active === index && styles.tabOn)}
                  onClick={() => {
                    clearTimer();
                    setActive(index);
                  }}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <div className={styles.stats}>
              {tab.stats.map((stat) => (
                <span key={stat.label}>
                  <b>{stat.value}</b> {stat.label}
                </span>
              ))}
            </div>
          </div>

          <div className={styles.stage}>
            <Image
              key={tab.image}
              src={tab.image}
              alt={tab.alt}
              width={tab.imageWidth}
              height={tab.imageHeight}
              className={styles.stageImage}
            />
          </div>

          <div className={styles.verdict}>
            <span className={styles.verdictDot} />
            <span>{tab.verdict}</span>
          </div>

          <div className={styles.cardFoot}>{tab.foot}</div>
        </div>
      </div>
    </Section>
  );
};
